import { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { identifyUser, resetUser } from "@/lib/analytics";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "TOKEN_REFRESHED" && !session) {
          // Token refresh failed — force sign out
          await supabase.auth.signOut();
          setSession(null);
          setLoading(false);
          toast({
            title: "Session expired",
            description: "Please sign in again.",
            variant: "destructive",
          });
          return;
        }
        if (event === "SIGNED_OUT") {
          resetUser();
          setSession(null);
          setLoading(false);
          return;
        }
        setSession(session);
        if (session?.user) {
          identifyUser(session.user.id, { email: session.user.email ?? undefined });
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        const msg = error.message ?? "";
        if (
          msg.includes("Invalid Refresh Token") ||
          msg.includes("Already Used") ||
          msg.includes("Refresh Token Not Found")
        ) {
          supabase.auth.signOut();
          toast({
            title: "Session expired",
            description: "Please sign in again.",
            variant: "destructive",
          });
        }
      }
      if (session?.user) {
        identifyUser(session.user.id, { email: session.user.email ?? undefined });
      }
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const authValue = useMemo(
    () => ({ session, user: session?.user ?? null, loading, signOut }),
    [session, loading, signOut]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}
